import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, FieldArray, Form, Formik } from "formik";
import performRequest from "../../../api/request";
import { Toast } from "../../../CustomComponent/toast";
import LoadingPage from "../../../Components/layouts/loading";
import {
  EmployeeLeaveNewSchema,
  EmployeeLeaveSchema,
} from "../../../Schemas/employee/employee_leave_schema";
import CustomInput from "../../../CustomComponent/custom_input";
import FormSubmitButton from "../../../Components/layouts/formSubmitButton";
import SimpleButton from "../../../Components/layouts/simple_button";
import Footer from "../../../Components/Footer/footer";
import { useSelector } from "react-redux";

import { BsCalendar2XFill } from "react-icons/bs";
import { FcCalendar } from "react-icons/fc";
import { CustomErrorResponse } from "../../../CustomComponent/custom_error_response";
import { EmployeeLeavesInfo } from "../../../SchemaInfo/info";
import CustomRadioButton from "../../../CustomComponent/custom_radio";
import CustomTextArea from "../../../CustomComponent/custom_textarea";
import CustomSelect from "../../../CustomComponent/custom_select";
import { LazyImageComponent } from "../../../CustomComponent/lazy_loading_image";
const getLeavesTaken = (obj) => {
  if (obj.leaveType === "half day") {
    return 0.5;
  }
  if (obj.leaveType === "full day") {
    return 1;
  } else {
    const start = new Date(obj.leaveStartDate);
    const end = new Date(obj.leaveEndDate);
    return (end - start) / (1000 * 60 * 60 * 24) + 1;
  }
};
const checkLeaveOverlap = (leavesDetails, values) => {
  const start = new Date(values.leaveStartDate || values.leaveDate);
  const end = new Date(values.leaveEndDate || values.leaveDate);

  return leavesDetails.some((leave) => {
    const leaveStart = new Date(leave.leaveStartDate || leave.leaveDate);
    const leaveEnd = new Date(leave.leaveEndDate || leave.leaveDate);
    const leaveEndStatus = leave.leaveStatus;
    return (
      (start >= leaveStart &&
        start <= leaveEnd &&
        leaveEndStatus != "rejected") ||
      (end >= leaveStart && end <= leaveEnd && leaveEndStatus != "rejected") ||
      (start <= leaveStart && end >= leaveEnd && leaveEndStatus != "rejected")
    );
  });
};
const EditLeaves = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [employeeLeavesInfo, setEmployeeLeavesInfo] =
    useState(EmployeeLeavesInfo);
  const [today, setToday] = useState(new Date()?.toISOString().split("T")[0]);
  const [today2, setToday2] = useState(new Date());
  const [leaveCount, setLeaveCount] = useState({ paid: 0, unpaid: 0 });
  let role = useSelector((store) => store?.login?.user?.Role);
  const [disableButton, setDisableButton] = useState(false);
  const navigate = useNavigate();
  let { id, index } = useParams();

  useEffect(() => {
    setLoading(true);
    performRequest("GET", `/leave/getleavesById/${id}`)
      .then((res) => {
        if (res.status === 200) {
          const fetchedData = res.data;
          if (fetchedData?.leavesDetails?.[index]) {
            setEmployeeLeavesInfo(fetchedData?.leavesDetails?.[index]);
          }
          const paidLeaves = fetchedData?.leavesDetails
            .filter((leave) => leave.leaveReasonType === "sick")
            .reduce((total, leave) => total + getLeavesTaken(leave), 0);
          const unpaidLeaves = fetchedData?.leavesDetails
            .filter((leave) => leave.leaveReasonType === "casual")
            .reduce((total, leave) => total + getLeavesTaken(leave), 0);

          setLeaveCount({ paid: paidLeaves, unpaid: unpaidLeaves });
          setData(fetchedData);
        } else {
          Toast("error", "Error while getting leave data!");
        }
        setLoading(false);
      })
      .catch((err) => {
        CustomErrorResponse(err);
        setLoading(false);
      });
  }, [id]);

  const handleSubmit = (values, actions) => {
    setDisableButton(true);
    let dataNew = data?.leavesDetails?.filter((_, i) => i !== parseInt(index));

    if (dataNew && checkLeaveOverlap(dataNew, values)) {
      actions.setFieldError(
        "leaveDate",
        "You have already taken a leave on this day."
      );
      return;
    }

    dataNew.splice(index, 0, values);

    let supportingDocument = values.supportingDocument || "";
    if (values.supportingDocument instanceof window.File) {
      let formData = new FormData();
      formData.append("file", values.supportingDocument);
      const headers = { "Content-Type": "multipart/form-data" };
      performRequest("POST", `/leave/fileupload/`, formData, headers)
        .then((res) => {
          if (res.status === 201) {
            supportingDocument = res.data.file;
            const updatedLeavesDetails = dataNew.map((leave, i) => {
              if (i === parseInt(index) && leave.leaveStatus === "approved") {
                return {
                  ...leave,
                  supportingDocument,
                  leaveApprovedDate: new Date().toISOString().split("T")[0],
                };
              } else if (i === parseInt(index)) {
                return {
                  ...leave,
                  supportingDocument,
                };
              }
              return leave;
            });

            const updatedData = {
              ...data,
              leavesDetails: updatedLeavesDetails,
            };
            uploadLeaves(updatedData);
          } else {
            Toast("error", "Error while uploading file!");
            setDisableButton(false);
          }
          setLoading(false);
        })
        .catch((err) => {
          CustomErrorResponse(err);
          setLoading(false);
          setDisableButton(false);
        });
    } else {
      const updatedLeavesDetails = dataNew.map((leave, i) => {
        if (i === parseInt(index) && leave.leaveStatus === "approved") {
          return {
            ...leave,
            supportingDocument,
            leaveApprovedDate: new Date().toISOString().split("T")[0],
          };
        } else if (i === parseInt(index)) {
          return {
            ...leave,
            supportingDocument,
          };
        }
        return leave;
      });

      const updatedData = {
        ...data,
        leavesDetails: updatedLeavesDetails,
      };
      uploadLeaves(updatedData);
    }
  };

  const uploadLeaves = (values) => {
    setLoading(true);
    performRequest("PATCH", `/leave/updateleaves/${id}`, values)
      .then((res) => {
        if (res.status === 200) {
          Toast("success", "Leave data updated successfully!");
          navigate(`/employee-leave-management/view/${id}`);
        } else {
          Toast("error", "Error while updating leave data!");
        }
        setLoading(false);
      })
      .catch((err) => {
        CustomErrorResponse(err);
      })
      .finally(() => {
        setLoading(false);
        setDisableButton(false);
      });
  };

  const fetchRealTime = () => {
    performRequest("GET", "/attendance/getCurrentTime/")
      .then((res) => {
        if (res.data?.datetime) {
          let date = new Date(res.data?.datetime);
          setToday(date?.toISOString().split("T")[0]);
          setToday2(date);
        }
      })
      .catch((err) => CustomErrorResponse(err));
  };

  useEffect(() => {
    fetchRealTime();
  }, []);

  let tomorrow = today2;
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow = tomorrow.toISOString().split("T")[0];

  if (loading || !role) {
    return <LoadingPage />;
  }

  return (
    <div className="page">
      <div className="page-wrapper">
        <div className="container-xxl">
          <div className="page-header d-print-none">
            <div className="container-xxl">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h2 className="page-title mb-1">
                    <FcCalendar className="fs-2  me-1" /> Update Leaves
                  </h2>
                  <h4 className="text-muted">
                    {/* <StepRoutes /> */} Update leaves status.
                  </h4>
                </div>
                <div className="col-md-4">
                  <div className="btn-list justify-content-end">
                    <a href={"/employee-leave-management"}>
                      <button className="btn btn-primary">
                        <BsCalendar2XFill className="me-2 fs-2" /> VIEW ALL
                        Leaves
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-3 col-sm-12 py-2">
              <div className="card">
                {data && (
                  <div className="table-responsive">
                    <table className="table table-vcenter card-table table-striped">
                      <thead>
                        <tr>
                          <th>Specs.</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="text-secondary">Account Name</td>
                          <td>{(data?.employeeName || "N/A").toUpperCase()}</td>
                        </tr>
                        <tr>
                          <td className="text-secondary">Employee Id</td>
                          <td>{(data?.userId || "N/A").toUpperCase()}</td>
                        </tr>
                        <tr>
                          <td className="text-secondary">Total Leaves</td>
                          <td>{data?.totalLeaves || "0"}</td>
                        </tr>
                        <tr>
                          <td className="text-secondary">Pending Leaves</td>
                          <td>{data?.pendingLeaves || "0"}</td>
                        </tr>
                        <tr>
                          <td className="text-secondary">Sick Leaves</td>
                          <td>{leaveCount.paid} (Obtained)</td>
                        </tr>
                        <tr>
                          <td className="text-secondary">Casual Leaves</td>
                          <td>{leaveCount.unpaid} (Obtained)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-8 col-sm-12">
              <div className="card">
                <Formik
                  initialValues={employeeLeavesInfo}
                  validationSchema={EmployeeLeaveNewSchema}
                  onSubmit={handleSubmit}
                >
                  {(props) => (
                    <Form>
                      <div className="card-body row">
                        <h4>
                          <span class="badge bg-purple-lt me-1">
                            Total leaves:{" "}
                            {data?.employeementStatus === "activePosition"
                              ? 15
                              : 0}
                          </span>
                          <span class="badge bg-indigo-lt me-1">
                            Sick leaves taken: {leaveCount.paid}
                          </span>
                          <span class="badge bg-green-lt me-1">
                            Casual leaves taken: {leaveCount.unpaid}
                          </span>
                          <span class="badge bg-yellow-lt me-1">
                            Pending leaves:{" "}
                            {data?.employeementStatus === "activePosition"
                              ? Math.max(
                                  15 - (leaveCount.paid + leaveCount.unpaid),
                                  0
                                )
                              : 0}
                          </span>
                        </h4>
                        <hr />
                        <div className="mb-3 row">
                          <label className="w-25 form-label required col-3">
                            Select Leave duration
                          </label>
                          <div className="w-100 col">
                            <div>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveType"
                                  value="half day"
                                  checked={
                                    props.values.leaveType === "half day"
                                  }
                                  disabled={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                  }
                                />
                                <span className="form-check-label">
                                  Half day
                                </span>
                              </label>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveType"
                                  value="full day"
                                  checked={
                                    props.values.leaveType === "full day"
                                  }
                                  disabled={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                  }
                                />
                                <span className="form-check-label">
                                  Full day
                                </span>
                              </label>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveType"
                                  value="multiple days"
                                  checked={
                                    props.values.leaveType === "multiple days"
                                  }
                                  disabled={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                  }
                                />
                                <span className="form-check-label">
                                  Multiple days
                                </span>
                              </label>
                            </div>
                            <ErrorMessage
                              name="leaveType"
                              component="p"
                              className="text-danger"
                            />
                          </div>
                        </div>
                        {props.values.leaveType === "half day" && (
                          <div className="mb-3 row">
                            <label className="w-25 form-label required col-3">
                              Half day type
                            </label>
                            <div className="w-100 col">
                              <div>
                                <label className="form-check form-check-inline">
                                  <CustomRadioButton
                                    name="halfDayType"
                                    value="first half"
                                    checked={
                                      props.values.halfDayType === "first half"
                                    }
                                    disabled={
                                      today >= props.values.leaveDate ||
                                      props.values.leaveStatus === "approved" ||
                                      props.values.leaveStatus === "rejected"
                                    }
                                  />
                                  <span className="form-check-label">
                                    First half
                                  </span>
                                </label>
                                <label className="form-check form-check-inline">
                                  <CustomRadioButton
                                    name="halfDayType"
                                    value="second half"
                                    checked={
                                      props.values.halfDayType === "second half"
                                    }
                                    disabled={
                                      today >= props.values.leaveDate ||
                                      props.values.leaveStatus === "approved" ||
                                      props.values.leaveStatus === "rejected"
                                    }
                                  />
                                  <span className="form-check-label">
                                    Second half
                                  </span>
                                </label>
                              </div>
                              <ErrorMessage
                                name="halfDayType"
                                component="p"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        )}
                        {props.values.leaveType !== "multiple days" && (
                          <div className="mb-3 row">
                            <label className="w-25 form-label required col-3">
                              Leave Date
                            </label>
                            <div className="w-100 col">
                              <CustomInput
                                name="leaveDate"
                                type="date"
                                disabled={
                                  today >= props.values.leaveDate ||
                                  props.values.leaveStatus === "approved" ||
                                  props.values.leaveStatus === "rejected"
                                }
                                min={
                                  today >= props.values.leaveDate ||
                                  props.values.leaveStatus === "approved" ||
                                  props.values.leaveStatus === "rejected"
                                    ? ""
                                    : tomorrow
                                }
                              />
                              <ErrorMessage
                                name="leaveDate"
                                component="p"
                                className="text-danger"
                              />
                            </div>
                          </div>
                        )}
                        {props.values.leaveType === "multiple days" && (
                          <>
                            <div className="mb-3 row">
                              <label className="w-25 form-label required col-3">
                                Start date
                              </label>
                              <div className="w-100 col">
                                <CustomInput
                                  name="leaveStartDate"
                                  type="date"
                                  onChange={(e) => {
                                    props.setFieldValue(
                                      "leaveStartDate",
                                      e.target.value
                                    );
                                    props.setFieldValue(
                                      "leaveDate",
                                      e.target.value
                                    );
                                  }}
                                  disabled={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                  }
                                  min={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                      ? ""
                                      : tomorrow
                                  }
                                />
                                <ErrorMessage
                                  name="leaveStartDate"
                                  component="p"
                                  className="text-danger"
                                />
                              </div>
                            </div>
                            <div className="mb-3 row">
                              <label className="w-25 form-label required col-3">
                                End date
                              </label>

                              <div className="w-100 col">
                                <CustomInput
                                  name="leaveEndDate"
                                  type="date"
                                  disabled={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                  }
                                  min={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                      ? ""
                                      : tomorrow
                                  }
                                />
                                <ErrorMessage
                                  name="leaveEndDate"
                                  component="p"
                                  className="text-danger"
                                />
                              </div>
                            </div>
                          </>
                        )}

                        <div className="mb-3 row">
                          <label className="w-25 form-label required col-3">
                            Reason type
                          </label>
                          <div className="w-100 col">
                            <div>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveReasonType"
                                  value="sick"
                                  checked={
                                    props.values.leaveReasonType === "sick"
                                  }
                                  disabled={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                  }
                                />
                                <span className="form-check-label">Sick</span>
                              </label>
                              <label className="form-check form-check-inline">
                                <CustomRadioButton
                                  name="leaveReasonType"
                                  value="casual"
                                  checked={
                                    props.values.leaveReasonType === "casual"
                                  }
                                  disabled={
                                    today >= props.values.leaveDate ||
                                    props.values.leaveStatus === "approved" ||
                                    props.values.leaveStatus === "rejected"
                                  }
                                />
                                <span className="form-check-label">
                                  Casual / Comp off
                                </span>
                              </label>
                            </div>
                            <ErrorMessage
                              name="leaveReasonType"
                              component="p"
                              className="text-danger"
                            />
                          </div>
                        </div>

                        <div className="mb-3 row">
                          <label className="w-25 form-label required col-3">
                            Leave Reason
                          </label>
                          <div className="w-100 col">
                            <CustomTextArea
                              name="leaveReason"
                              type="text"
                              disabled={
                                today >= props.values.leaveDate ||
                                props.values.leaveStatus === "approved" ||
                                props.values.leaveStatus === "rejected"
                              }
                            />
                            <ErrorMessage
                              name="leaveReason"
                              component="p"
                              className="text-danger"
                            />
                          </div>
                        </div>
                        <div className="mb-3 row">
                          <label className="w-25 form-label col-3">
                            Leave Status
                          </label>
                          <div className="w-100 col">
                            <CustomSelect
                              name={`leaveStatus`}
                              disabled={
                                !(
                                  role === process.env.REACT_APP_ROLE_ADMIN_S ||
                                  role === process.env.REACT_APP_ROLE_HR ||
                                  role === process.env.REACT_APP_ROLE_ADMIN
                                )
                              }
                            >
                              <option value="">Select</option>
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                            </CustomSelect>
                            <ErrorMessage
                              name="leaveStatus"
                              component="p"
                              className="text-danger"
                            />
                          </div>
                        </div>

                        <div className="mb-3 row">
                          <label className="w-25 form-label col-3">
                            Supporting Document
                          </label>
                          <div className="w-100 col">
                            <CustomInput
                              name="supportingDocument"
                              type="file"
                              disabled={
                                props.values.leaveStatus === "approved" ||
                                props.values.leaveStatus === "rejected"
                              }
                              onChange={(e) =>
                                props.setFieldValue(
                                  "supportingDocument",
                                  e.target.files[0]
                                )
                              }
                            />

                            <ErrorMessage
                              name="supportingDocument"
                              component="p"
                              className="text-danger"
                            />
                            {!props.values.supportingDocument && (
                              <p className="text-danger">
                                Submit the supporting document before your
                                leave. It must be submitted within 2 days after
                                the leave end date to avoid automatic rejection.
                              </p>
                            )}
                          </div>
                        </div>

                        {props.values.supportingDocument &&
                          !(
                            props.values.supportingDocument instanceof
                            window.File
                          ) && (
                            <div className="mb-3 row">
                              <label className="w-25 form-label col-3">
                                Supporting Document uploaded
                              </label>
                              <div className="w-100 col">
                                <a
                                  href={props.values.supportingDocument}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <LazyImageComponent
                                    src={props.values.supportingDocument}
                                    alt={"Supporting Document uploaded"}
                                  />
                                </a>
                              </div>
                            </div>
                          )}
                      </div>
                      <div className="card-footer">
                        <div className="btn-list justify-content-end">
                          <FormSubmitButton
                            buttonText="Edit Leaves"
                            disabled={disableButton}
                          />
                          <SimpleButton buttonText="Cancel" type="secondary" />
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
              <div className="card p-3 my-2">
                <ul>
                  <li>
                    During the probation or notice period, all leaves will be
                    unpaid.
                  </li>

                  <li>
                    You can avail a maximum of 3 compensatory-off (comp-off)
                    leaves in a month.
                  </li>

                  <li className="text-orange">
                    For sick leave, you must submit a doctor’s prescription. For
                    Casual leave, you must submit a written leave document
                    signed and approved by two members of your higher authority.
                  </li>

                  <li>
                    All required documents must be submitted within 2 days after
                    the leave end date for the leave to be approved.
                  </li>

                  <li>
                    If the required documents are not submitted within the
                    specified time, the system will automatically reject the
                    leave request.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default EditLeaves;
